import React, {useMemo, useRef} from 'react';
import {useReactToPrint} from "react-to-print";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import {Print as PrintIcon} from "@mui/icons-material";
import {ClosableDialogTitle} from "@atttomyx/react-components";
import {datetime, formatters, money, users} from "@atttomyx/shared-utils";
import "./taxDialog.css";

const PrintableTaxStatement = React.forwardRef((props, ref) => {
    const {account, node, year, offerings, total} = props;

    return (
        <div ref={ref} className="printable-content">
            <div className="blurb">
                <Typography variant="caption" color="text.secondary" paragraph={true}>
                    This statement contains a record of voluntary contributions to {account.name} made by {users.getFullName(node)} during the year {year}.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {account.name} provided no goods or services in consideration, in whole or in part, for the contributions detailed below but only intangible religious benefits.
                </Typography>
            </div>
            <Divider/>
            <Table size="small" cellPadding={0} cellSpacing={0} className="tax-table">
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {offerings && offerings.length > 0 ? (
                        offerings.map((offering) => (
                            <TableRow key={offering.id}>
                                <TableCell>{datetime.getPrettyDateShort(offering.date)}</TableCell>
                                <TableCell>{offering.category}</TableCell>
                                <TableCell>{offering.type}</TableCell>
                                <TableCell>{formatters.usDollars(offering.amount)}</TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={4}>No offerings found for this year.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {offerings && offerings.length > 0 && (
                <>
                    <Divider/>
                    <Table size="small" cellPadding={0} cellSpacing={0}>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={2} align="right"><b>Total:</b></TableCell>
                                <TableCell><b>{formatters.usDollars(total)}</b></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </>
            )}
        </div>
    );
});

const TaxDialog = (props) => {
    const {account, node, year, offerings, onCancel} = props;

    const total = useMemo(() => {
        const monies = offerings.map(offering => offering.amount);
        return money.add(monies);
    }, [offerings]);

    const title = useMemo(() => {
        return "Tax Statement for " + year;
    }, [year]);

    const componentRef = useRef(null);
    const printStatement = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: title + " - " + users.getFullName(node),
        pageStyle: `
            @media print {
                body {
                    margin-top: 20px !important;
                }
                .printable-content,
                .printable-content * {
                    color: black !important;
                }
                .printable-content hr,
                .printable-content .MuiDivider-root {
                    border-color: black !important;
                    display: block !important;
                }
            }
        `,
    });

    return (
        <>
            <Dialog
                open={true}
                aria-labelledby="tax-dialog"
            >
                <ClosableDialogTitle onClose={onCancel}>
                    {title}
                </ClosableDialogTitle>
                <DialogContent className="tax-dialog">
                    <Divider/>
                    <PrintableTaxStatement
                        account={account}
                        node={node}
                        year={year}
                        offerings={offerings}
                        total={total}
                    />
                </DialogContent>
                <DialogActions>
                    <IconButton
                        color="primary"
                        title="Print"
                        onClick={printStatement}
                    >
                        <PrintIcon/>
                    </IconButton>
                </DialogActions>
            </Dialog>
            <div className="hide-on-screen">
                <PrintableTaxStatement
                    ref={componentRef}
                    account={account}
                    node={node}
                    year={year}
                    offerings={offerings}
                    total={total}
                />
            </div>
        </>
    );
};

export default TaxDialog;
